import { InputSelect, InputWrapper } from '@vrplatform/components';
import { useInfiniteQuery, useQuery, useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import type { SelectItem } from '@vrplatform/components';
import { toTitleCase } from '@vrplatform/utils';
import { Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { contact_bool_exp } from '@vrplatform/graphql';
import {
  formatOwnerName,
  orderByOwner,
  whereContacts,
} from '@vrplatform/ui-common';
import { useCallback, useState } from 'react';
import { Controller } from 'react-hook-form';
import { VendorDrawer } from '../../../../drawers/vendor-drawer';
import { useExpenseForm } from '../useExpenseForm';

export const ExpenseContactInput = () => {
  const methods = useExpenseForm();
  const [{ id: teamId }] = useTeam();

  const [opened, handlers] = useDisclosure();
  const [search, setSearch] = useState('');

  const queryData = useInfiniteQuery(
    (q, { teamId, search }, { limit, offset }) => {
      if (!teamId)
        return {
          list: [],
          aggregate: 0,
        };

      const where: contact_bool_exp = whereContacts({
        search,
        tenantId: teamId,
        type: null,
      });

      const list = q
        .contacts({
          where,
          order_by: [{ type: 'desc_nulls_last' }, orderByOwner],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: formatOwnerName(res) || 'No name',
          group: toTitleCase(res.type || '-'),
        }));

      const aggregate = q.contactAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      queryKey: ['contacts'],
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );
  return (
    <Box>
      <Controller
        control={methods.control}
        name={'contactId'}
        rules={{
          required: 'Please select a contact',
        }}
        render={({ field, fieldState }) => {
          const { data, isLoading: loading } = useQuery(
            (q, args) => {
              if (!args.value)
                return {
                  contact: null,
                };

              const contact = q
                .contacts({
                  where: {
                    id: { _eq: args.value },
                  },
                })
                .map<SelectItem>((item) => ({
                  label: formatOwnerName(item) || 'No name',
                  value: item.id!,
                }))[0];

              return {
                contact,
              };
            },
            {
              keepPreviousData: true,
              variables: {
                value: field.value,
              },
            }
          );

          const contact = field.value ? data?.contact || null : null;

          const onContactAdded = useCallback(
            (contactId: string) => {
              field.onChange(contactId);
            },
            [field.onChange]
          );

          return (
            <>
              <VendorDrawer
                closeDrawer={handlers.close}
                opened={opened}
                onVendorAdded={onContactAdded}
              />

              <InputWrapper error={fieldState.error?.message}>
                <InputSelect
                  type="single"
                  value={contact}
                  data-testid={field.name}
                  name={field.name}
                  setValue={(v) => field.onChange(v?.value || null)}
                  infiniteData={{ ...queryData, setSearch }}
                  inputProps={{
                    error: !!fieldState.error,
                    placeholder: 'Select contact',
                    loadingQuery: loading,
                    withClearButton: true,
                    disabled:
                      field.disabled ||
                      methods.context.lock?.expense === 'locked' ||
                      methods.context.lock?.payment === 'locked',
                  }}
                  dropdownProps={{
                    width: 'target',
                  }}
                  customBottomActions={[
                    {
                      id: 'add-vendor',
                      label: 'Add vendor',
                      onSubmit: () => handlers.open(),
                      description:
                        "Add a new vendor in case you're missing one",
                      icon: <Icon icon="PlusIcon" size={16} />,
                    },
                  ]}
                />
              </InputWrapper>
            </>
          );
        }}
      />
    </Box>
  );
};
